import { home } from "@/content";
import { Rich } from "@/lib/rich";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export default function LightProcess() {
  const { process: section } = home;
  const { ref: headerRef, revealed: headerRevealed } = useScrollReveal();

  return (
    <section id="process" className="py-24 bg-[#FAFAF8] dark:bg-[#0A0A0A] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[8%] right-[-5%] w-[500px] h-[500px] rounded-full bg-orange-200/25 dark:bg-orange-500/[0.12] blur-[140px]" />
        <div className="absolute bottom-[5%] left-[-5%] w-[400px] h-[400px] rounded-full bg-green-300/20 dark:bg-green-500/10 blur-[130px]" />
        <div className="absolute top-[50%] right-[20%] w-[350px] h-[350px] rounded-full bg-violet-300/18 dark:bg-violet-500/10 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-700 ${
            headerRevealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-stone-100 dark:bg-white/[0.06] border border-stone-200 dark:border-white/[0.10] text-stone-600 dark:text-white/70 text-xs font-semibold tracking-widest uppercase mb-6">
            {section.badge}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-stone-900 dark:text-white leading-tight mb-4">
            <Rich text={section.title} accentClassName="text-orange-700 dark:text-orange-400" />
          </h2>
          {section.subtitle && (
            <p className="text-stone-600 dark:text-white/65 text-lg leading-relaxed max-w-2xl mx-auto">
              {section.subtitle}
            </p>
          )}
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-[72px] left-[12%] right-[12%] h-px bg-gradient-to-r from-stone-200 dark:from-white/10 via-amber-300 dark:via-orange-400/40 to-stone-200 dark:to-white/10" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {section.steps.map((step, index) => (
              <StepCard key={step.num} step={step} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function StepCard({
  step,
  index,
}: {
  step: { num: string; icon: string; title: string; text: string };
  index: number;
}) {
  const { ref, revealed } = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`group relative flex flex-col items-center text-center transition-all duration-700 hover:-translate-y-1 cursor-default ${
        revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-amber-50 dark:bg-orange-500/10 border border-amber-200 dark:border-orange-500/20 mb-5 relative z-10 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-amber-500/15 dark:group-hover:shadow-orange-500/20 group-hover:border-amber-300 dark:group-hover:border-orange-400/40">
        <i
          className={`${step.icon} text-amber-600 dark:text-amber-500 text-2xl transition-transform duration-300 group-hover:scale-110`}
        ></i>
      </div>
      <span className="text-amber-600 dark:text-amber-500 font-extrabold text-2xl mb-2">{step.num}</span>
      <h4 className="text-stone-900 dark:text-white font-bold text-base mb-2">{step.title}</h4>
      <p className="text-stone-600 dark:text-white/65 text-sm leading-relaxed">{step.text}</p>
    </div>
  );
}
